"use client"

import * as React from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"

import { CompanyStep } from "@/components/signup/company-step"
import { StepIndicator } from "@/components/common/step-indicator"
import { UserStep } from "@/components/signup/user-step"
import { buttonVariants } from "@/components/ui/button"
import { useAuth } from "@/lib/auth/auth-context"
import { type InvitationPreview } from "@/lib/api/types"
import { type UserStepValues } from "@/lib/signup/schemas"
import { cn } from "@/lib/utils"
import * as m from "@/paraglide/messages"

interface SignupFlowProps {
  invitationToken?: string | null
  invitationPreview?: InvitationPreview | null
  invitationInvalid?: boolean
}

export function SignupFlow({
  invitationToken = null,
  invitationPreview = null,
  invitationInvalid = false,
}: SignupFlowProps) {
  const router = useRouter()
  const { user } = useAuth()
  const [step, setStep] = React.useState<1 | 2>(user ? 2 : 1)
  const [userValues, setUserValues] = React.useState<UserStepValues | undefined>()

  const hasInvitation = Boolean(invitationToken)

  const steps = [
    { label: m.signup_step_label_user() },
    { label: m.signup_step_label_company() },
  ]

  const handleUserSuccess = (values: UserStepValues) => {
    setUserValues(values)
    if (hasInvitation) {
      // Backend already attached the new user to the inviter's company.
      router.push("/")
      router.refresh()
      return
    }
    setStep(2)
  }

  const handleCompanySuccess = () => {
    router.push("/")
    router.refresh()
  }

  const canGoBack = !user && !hasInvitation

  if (invitationInvalid) {
    return (
      <div className="grid gap-4">
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          {m.signup_invitation_invalid()}
        </div>
        <Link
          href="/signup"
          className={cn(buttonVariants({ variant: "outline" }))}
        >
          {m.signup_invitation_start_fresh()}
        </Link>
      </div>
    )
  }

  if (hasInvitation && invitationPreview?.userExists && !user) {
    const loginHref = `/login?invitation_token=${encodeURIComponent(invitationToken ?? "")}`
    return (
      <div className="grid gap-4">
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
          {m.signup_invitation_existing_user_prompt({
            company: invitationPreview.companyName,
          })}
        </div>
        <Link href={loginHref} className={cn(buttonVariants())}>
          {m.signup_invitation_login_action()}
        </Link>
      </div>
    )
  }

  return (
    <div className="grid gap-6">
      {invitationPreview ? (
        <div
          role="status"
          className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm"
        >
          <p>
            {invitationPreview.inviter
              ? m.signup_invitation_banner({
                  company: invitationPreview.companyName,
                  inviterName: `${invitationPreview.inviter.name} ${invitationPreview.inviter.lastName}`.trim(),
                })
              : m.signup_invitation_banner_generic({
                  company: invitationPreview.companyName,
                })}
          </p>
          {invitationPreview.projects.length > 0 ? (
            <div className="mt-2">
              <p className="text-xs font-medium text-muted-foreground">
                {m.signup_invitation_projects_label()}
              </p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {invitationPreview.projects.map((project) => (
                  <li
                    key={project.id}
                    className="rounded-md bg-background px-2 py-0.5 text-xs ring-1 ring-foreground/10"
                  >
                    {project.name}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {hasInvitation ? null : (
        <StepIndicator
          steps={steps}
          currentStep={step}
          ariaLabel={m.signup_progress_label()}
        />
      )}

      {step === 1 ? (
        <UserStep
          defaultValues={{
            ...userValues,
            email: invitationPreview?.email ?? userValues?.email ?? "",
          }}
          invitationToken={invitationToken ?? undefined}
          lockEmail={Boolean(invitationPreview)}
          onSuccess={handleUserSuccess}
        />
      ) : (
        <CompanyStep
          onBack={canGoBack ? () => setStep(1) : undefined}
          onSuccess={handleCompanySuccess}
        />
      )}

      {!user ? (
        <p className="text-center text-sm text-muted-foreground">
          {m.signup_login_prompt()}{" "}
          <Link
            href="/login"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {m.signup_login_link()}
          </Link>
        </p>
      ) : null}
    </div>
  )
}
